import { useFetcher } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { ShoppingBagIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { PublicCartPriceDetail } from '~/helpers/cart'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import type { CartFull } from '~/models/cart.server'
import type { ShippingCountryCode } from '~/schemas/country'
import { Theme } from '~/themes/Theme'
import { Alert, Badge, Button, Divider, Drawer, Skeleton } from '~/ui'
import { cn } from '~/utils/classnames'
import { isNumber } from '~/utils/number'

import { useCartContext } from './cart-context'
import { CartItemCard } from './cart-item-card'
import { CartPriceDetails } from './cart-price-details'
import { CartPromotions } from './cart-promotions'
import { CartShipping } from './cart-shipping'
import { Price } from '../price'
import { getTotalCartProducts } from './cart-utils'

interface CartDrawerProps {
  cart: SerializeFrom<CartFull> | null
  priceDetail: SerializeFrom<PublicCartPriceDetail> | null
  buyerCountry?: ShippingCountryCode
}

export const CartDrawer = ({
  cart,
  priceDetail,
  buyerCountry,
}: CartDrawerProps) => {
  const { t } = useTranslation()

  const shop = useShop()
  const fetcher = useFetcher()
  const submitFetcher = useFetcher()

  const { isDrawerOpen, setIsDrawerOpen } = useCartContext()

  const numProducts = useMemo(
    () => (cart ? getTotalCartProducts(cart) : 0),
    [cart]
  )

  // don't very much like this, but it works for now
  const submitError = useMemo(() => {
    if (
      submitFetcher.data &&
      typeof submitFetcher.data === 'object' &&
      'error' in submitFetcher.data
    ) {
      return (submitFetcher.data as { error?: string }).error
    }
    return undefined
  }, [submitFetcher.data])

  const handleSubmit = () => {
    submitFetcher.submit(null, {
      method: 'POST',
      action: makeShopOwnPath(shop, '/checkout?index'),
    })
  }

  const hasCart = !!cart && numProducts > 0
  const isDataLoading = fetcher.state !== 'idle'
  const isSubmitting = submitFetcher.state !== 'idle'

  return (
    <Theme theme="shop">
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <Drawer.Content
          className="text-foreground"
          inert={submitFetcher.state !== 'idle'}
        >
          <Drawer.Header>
            <Drawer.Title className="flex items-center gap-1">
              <ShoppingBagIcon className="text-foreground size-4.5" />
              <p className="text-foreground text-sm font-medium">
                {t('components.cart.cart')}
              </p>
              {numProducts > 0 && (
                <Badge className="bg-accent-primary text-accent-foreground text-2xs/none min-w-4 justify-center tabular-nums">
                  {numProducts}
                </Badge>
              )}
            </Drawer.Title>
          </Drawer.Header>
          {!hasCart && (
            <div className="text-foreground flex flex-1 flex-col items-center justify-center px-4 font-medium">
              <p className="mb-0.5">
                {t('components.cart.empty_state.cart_is_empty')}
              </p>
              <p className="text-foreground/48 mb-5 text-center">
                {t('components.cart.empty_state.not_added_items')}
              </p>
              <Drawer.Close asChild>
                <Button>{t('action.continue_shopping')}</Button>
              </Drawer.Close>
            </div>
          )}
          {hasCart && (
            <>
              <Drawer.Section>
                <div className="mb-(--drawer-py) flex flex-col justify-center gap-2 empty:hidden">
                  <CartPromotions cart={cart} />
                </div>
                <ul className="space-y-2">
                  {cart.cartLines.map((item) => (
                    <li key={item.id}>
                      <CartItemCard fetcher={fetcher} cart={cart} item={item} />
                    </li>
                  ))}
                </ul>
              </Drawer.Section>
              <Drawer.Actions className="block space-y-(--drawer-py)">
                <CartShipping fetcher={fetcher} buyerCountry={buyerCountry} />
                <Drawer.Bleed>
                  <Divider />
                </Drawer.Bleed>
                <div>
                  <CartPriceDetails
                    fetcher={fetcher}
                    priceDetail={priceDetail}
                  />
                  <Button
                    className={cn(
                      'mt-4 -ml-0.5 w-[calc(100%+0.5rem)] justify-between',
                      submitError && 'animate-shake'
                    )}
                    size="lg"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                  >
                    <span>{t('action.proceed_checkout')}</span>
                    <span
                      className={cn(
                        'relative ml-5 border-l border-white/20 pl-5',
                        isSubmitting && 'opacity-0'
                      )}
                    >
                      <div
                        className={cn(
                          'relative',
                          isDataLoading && 'text-transparent'
                        )}
                      >
                        {isNumber(priceDetail?.totalAmount) && (
                          <Price amount={priceDetail.totalAmount} />
                        )}
                        {!isNumber(priceDetail?.totalAmount) && <span>-</span>}
                        {isDataLoading && (
                          <Skeleton className="absolute inset-0 rounded-md bg-white/16" />
                        )}
                      </div>
                    </span>
                  </Button>
                  {submitError && !isSubmitting && (
                    <Alert
                      type="error"
                      title={t(submitError)}
                      className="my-2"
                    />
                  )}

                  <p className="text-foreground/48 mt-2 text-xs font-medium">
                    {t('components.cart.info.taxed_included_additional_fees')}
                  </p>
                </div>
              </Drawer.Actions>
            </>
          )}
        </Drawer.Content>
      </Drawer>
    </Theme>
  )
}
